import { importerClient, endpoints } from '../api/client';
import { Channel, ListingState } from '../types/brandtraxListing';
import {
  applyClassificationToBrandTraxListing,
  buildAnalysisImportOptions,
} from './infringementReasons';
import { collectListingSellers } from './listingSellers';
import {
  transformReviewListingToBrandTrax,
  ReviewListing,
  StoreProduct,
  extractItemNumberFromUrl,
  resolveListingCountry,
} from './listingTransformer';
import { extractSellerIdentifierFromDefinition, resolveMarketplace } from './marketplaceMatcher';
import type { GroupedProductEntry } from './reviewListingGroups';
import { resolveSellerInjectUrl, resolveSellerMarketplace } from './injectSellerFields';
import { validateListingForInject } from './listingInjectValidation';
import { clearDefinitionWorkStatuses, definitionIdsForWorkClear } from './definitionWorkStatus';

export type InjectReviewListingParams = {
  product: any;
  group: GroupedProductEntry;
  catalogProduct: any | null;
  brand: string;
  productName: string;
  analysisProfile?: 'BRAND_IP_MERCHANDISE';
  productCategory?: string;
  keywords?: string[];
  listingClassification?: ListingState;
  infringementReason?: string;
  applyAiInfringement?: boolean;
  deletedImages?: Set<string>;
  discoveredSellers?: any[];
  findMarketplaceDefinitionForUrl: (url?: string) => any;
  /** Scraping task's definition. A successful inject clears its work marker. */
  marketplaceDefinitionId?: string | null;
};

export type InjectReviewListingResult = {
  ok: boolean;
  error?: string;
  sellerCount?: number;
};

function toStoreProduct(catalogProduct: any | null): StoreProduct | undefined {
  if (!catalogProduct) return undefined;
  return {
    name: catalogProduct.name,
    brand: catalogProduct.brand,
    description: catalogProduct.description,
    msrp: catalogProduct.msrp,
    category: catalogProduct.category,
    url: catalogProduct.url,
    images: catalogProduct.images,
  };
}

function listingImages(product: any, deletedImages?: Set<string>): string[] {
  const allImages = Array.isArray(product?.images) ? (product.images as string[]) : [];
  return allImages.filter(
    (img: string) =>
      typeof img === 'string' && img.trim() !== '' && !(deletedImages && deletedImages.has(img))
  );
}

function toReviewListing(product: any, filteredImages: string[]): ReviewListing {
  return {
    url: product.url || '',
    title: product.title || '',
    price: product.price,
    seller: product.seller || product.seller_name,
    marketplace: product.marketplace,
    image: product.image,
    images: filteredImages,
    description: product.description,
    stage: product.stage,
    screenshot_url: product.screenshot_url,
    brand: product.brand,
    product: product.product,
    itemNumber: product.itemNumber ?? product.item_number,
    item_number: product.item_number ?? product.itemNumber,
    relatedItems: product.relatedItems ?? product.related_items,
    related_items: product.related_items ?? product.relatedItems,
    sellerStoreUrl: product.sellerStoreUrl ?? product.seller_store_url,
    seller_store_url: product.seller_store_url ?? product.sellerStoreUrl,
  };
}

/**
 * Inject a Reviews listing into BrandTrax. Shared by the UI button and Auto-process.
 * Does not toast or mutate React state.
 */
export async function injectReviewListing(
  params: InjectReviewListingParams
): Promise<InjectReviewListingResult> {
  const {
    product,
    group,
    catalogProduct,
    brand,
    productName,
    analysisProfile,
    productCategory,
    keywords,
    listingClassification = ListingState.NEW,
    infringementReason = '',
    applyAiInfringement = false,
    deletedImages,
    discoveredSellers = [],
    findMarketplaceDefinitionForUrl,
    marketplaceDefinitionId,
  } = params;

  try {
    const analysisOptions = buildAnalysisImportOptions(
      listingClassification,
      Boolean(applyAiInfringement)
    );
    const filteredImages = listingImages(product, deletedImages);
    const reviewListing = toReviewListing(product, filteredImages);
    const storeMarketplace = await resolveMarketplace(reviewListing.url || '');
    const brandIpMerchandise = analysisProfile === 'BRAND_IP_MERCHANDISE';
    const brandTraxListing = await transformReviewListingToBrandTrax(
      reviewListing,
      brandIpMerchandise ? undefined : toStoreProduct(catalogProduct),
      brand,
      brandIpMerchandise ? undefined : productName,
      {
        marketplaceCountry: storeMarketplace.country,
        platformIdentifier: storeMarketplace.platform,
      },
      brandIpMerchandise ? { analysisProfile: 'BRAND_IP_MERCHANDISE', productCategory } : undefined
    );

    const marketplaceDef = findMarketplaceDefinitionForUrl(brandTraxListing.url);
    const definitionCurrency = marketplaceDef?.config?.currency || marketplaceDef?.currency;
    const definitionChannel =
      marketplaceDef?.channel || marketplaceDef?.config?.channel || Channel.MARKETPLACE;
    const channelValue =
      typeof definitionChannel === 'string'
        ? (definitionChannel.toUpperCase() as Channel)
        : definitionChannel || Channel.MARKETPLACE;
    const itemNumber =
      (brandTraxListing as any).itemNumber ||
      extractItemNumberFromUrl(brandTraxListing.url || product?.url || '');

    const allSellers = collectListingSellers({
      product,
      discovered: discoveredSellers,
      groupProducts: group.allProductsInGroup,
    });

    const buildNormalizedListing = (primarySellerCountry?: string | null) =>
      applyClassificationToBrandTraxListing(
        {
          ...brandTraxListing,
          ...(itemNumber ? { itemNumber } : {}),
          domain: marketplaceDef?.domain || brandTraxListing.domain,
          listedCurrency: definitionCurrency || (brandTraxListing as any).listedCurrency,
          channel: channelValue,
          ...(keywords?.length ? { keywords } : {}),
          country: resolveListingCountry({
            domain: marketplaceDef?.domain || brandTraxListing.domain,
            sellerCountry: primarySellerCountry,
            marketplaceCountry: storeMarketplace.country,
            platformIdentifier: brandTraxListing.platform ?? storeMarketplace.platform,
          }),
        },
        listingClassification,
        infringementReason
      );

    const listingSellerName = product.seller || product.seller_name;
    const sellersData =
      allSellers.length > 0
        ? allSellers.map((seller: any) => {
            let contact = seller.contact;
            if (!contact && seller.location) {
              contact = { address: seller.location };
            }
            const country = contact?.country || null;
            const sellerUrl = resolveSellerInjectUrl(seller, product);
            const sellerDef = findMarketplaceDefinitionForUrl(sellerUrl);
            const identifierFromDef = extractSellerIdentifierFromDefinition(
              sellerUrl,
              sellerDef ?? undefined
            );
            const identifier =
              seller.name ||
              seller.seller_name ||
              seller.identifier ||
              identifierFromDef ||
              (allSellers.length === 1 ? listingSellerName : null);
            return {
              identifier,
              marketplace: resolveSellerMarketplace(
                (brandTraxListing as any)?.platform,
                storeMarketplace.platform,
                product.marketplace
              ),
              url: sellerUrl,
              rating: seller.rating || seller.seller_rating,
              description: seller.description,
              brands: product.brand ? [product.brand] : [],
              country,
              contact,
            };
          })
        : listingSellerName
          ? [
              {
                identifier: listingSellerName,
                marketplace: resolveSellerMarketplace(
                  (brandTraxListing as any)?.platform,
                  storeMarketplace.platform,
                  product.marketplace
                ),
                url: resolveSellerInjectUrl(null, product),
                rating: product.seller_rating,
                description: product.description || product.seller_description,
                brands: product.brand ? [product.brand] : [],
                country: product.location,
              },
            ]
          : [];

    const normalizedListing = buildNormalizedListing(sellersData[0]?.country);
    const missingListing: string[] = [];
    if (!normalizedListing.title) missingListing.push('title');
    if (!(normalizedListing as any).seller) missingListing.push('seller');
    if (!normalizedListing.url) missingListing.push('listing URL');
    const missingSeller: string[] = [];
    if (sellersData.length === 0) {
      missingSeller.push('seller');
    } else {
      sellersData.forEach((s: any, i: number) => {
        const label = sellersData.length > 1 ? `seller ${i + 1}` : 'seller';
        if (!s.identifier) missingSeller.push(`${label} identifier`);
        if (!s.marketplace) missingSeller.push(`${label} marketplace`);
        if (!s.url) missingSeller.push(`${label} URL`);
      });
    }
    if (missingListing.length > 0 || missingSeller.length > 0) {
      return {
        ok: false,
        error: `Cannot send to BrandTrax. Missing: ${[...missingListing, ...missingSeller].join(', ')}`,
      };
    }

    const unreasonable = validateListingForInject(
      normalizedListing,
      toStoreProduct(catalogProduct)?.msrp
    );
    if (unreasonable) {
      return { ok: false, error: unreasonable };
    }

    const response = await importerClient.post(endpoints.importScraperListing, {
      listing: normalizedListing,
      sellers: sellersData,
      ...(analysisOptions ? { analysisOptions } : {}),
    });

    if (response.status === 200) {
      await clearDefinitionWorkStatuses(
        definitionIdsForWorkClear(marketplaceDefinitionId, marketplaceDef?.id)
      );
      return { ok: true, sellerCount: sellersData.length };
    }
    return { ok: false, error: 'Failed to inject listing - unexpected response' };
  } catch (error) {
    console.error('Error injecting listing:', error);
    return { ok: false, error: 'Error injecting listing to store' };
  }
}
